import React, { useEffect, useState, useMemo, useCallback } from 'react';
import AdminLayout from '../../components/AdminLayout';
import Loader from '../../components/Loader';
import AdminListViewControls from '../../components/AdminListViewControls';
import AdminSelectableList from '../../components/AdminSelectableList';
import { useNotification } from '../../context/NotificationContext';
import {
  getMirrors,
  getOngoingServices,
  cancelOngoingBooking
} from '../../services/adminApi';
import { useAdminInfiniteList, useNearBottomScroll } from '../../hooks/useAdminInfiniteList';
import { useAdminListView } from '../../hooks/useAdminListView';
import { normalizeAdminListResponse } from '../../utils/adminListResponse';
import BrandBranchFilterFields from '../../components/BrandBranchFilterFields';
import usePortalScope from '../../hooks/usePortalScope';
import { tenantListParams } from '../../utils/contentScope';
import './AdminPages.css';
import ExpandableText from '../../components/ExpandableText';

const OngoingServices = () => {
  const { showError, showSuccess, confirm } = useNotification();
  const portal = usePortalScope();
  const salons = portal.salons;
  const brands = portal.brands;
  const [mirrors, setMirrors] = useState([]);
  const [filters, setFilters] = useState({
    brandId: '',
    salonId: '',
    mirrorId: '',
    branch: ''
  });
  const [cancellingId, setCancellingId] = useState(null);
  const branchOptions = [...new Set((salons || []).map((s) => s.branch).filter(Boolean))];

  const filtersKey = useMemo(() => JSON.stringify(filters), [filters]);

  const getOngoingParams = useCallback(() => {
    const params = { ...tenantListParams(portal, filters) };
    if (filters.mirrorId) params.mirrorId = filters.mirrorId;
    if (filters.branch) params.branch = filters.branch;
    return params;
  }, [filters, portal]);

  const { items: rows, loading, loadingMore, pagination, loadMore, reload } = useAdminInfiniteList({
    load: getOngoingServices,
    resetKey: filtersKey,
    getParams: getOngoingParams,
    pageSize: 30
  });

  const scrollRef = useNearBottomScroll(loadMore, { enabled: !loading && pagination.hasMore });

  const listView = useAdminListView(rows, (row) => row._id, {
    defaultView: 'table',
    resetKey: filtersKey
  });

  useEffect(() => {
    const boot = async () => {
      try {
        const mirrorRes = await getMirrors({ all: true });
        const m = normalizeAdminListResponse(mirrorRes);
        setMirrors(m.data || []);
      } catch (error) {
        console.error('Error loading filter masters:', error);
      }
    };
    boot();
  }, []);

  const handleFilterChange = (k, v) => setFilters((p) => ({ ...p, [k]: v }));

  const handleClear = () => {
    setFilters({
      brandId: '',
      salonId: '',
      mirrorId: '',
      branch: ''
    });
  };

  const handleCancel = async (bookingId) => {
    const ok = await confirm({
      title: 'Cancel ongoing service',
      message:
        'This will mark the booking as cancelled. It will disappear from this list. The customer may need to start a new booking on the mirror.',
      confirmText: 'Cancel service',
      danger: true
    });
    if (!ok) return;
    setCancellingId(bookingId);
    try {
      await cancelOngoingBooking(bookingId);
      showSuccess('Service cancelled.');
      await reload();
    } catch (error) {
      showError(error.response?.data?.message || 'Failed to cancel');
    } finally {
      setCancellingId(null);
    }
  };

  const formatDate = (d) => {
    if (!d) return '-';
    const dt = new Date(d);
    if (Number.isNaN(dt.getTime())) return '-';
    return dt.toLocaleString('en-IN', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const renderCancelButton = (bookingId) => (
    <button
      type="button"
      className="btn btn-sm btn-danger"
      disabled={cancellingId === bookingId}
      onClick={() => handleCancel(bookingId)}
    >
      {cancellingId === bookingId ? 'Cancelling…' : 'Cancel / abort'}
    </button>
  );

  const ongoingColumns = useMemo(
    () => [
      {
        key: 'id',
        label: 'Booking',
        render: (r) => `#${String(r._id).slice(-8).toUpperCase()}`
      },
      { key: 'startedAt', label: 'Started', render: (r) => formatDate(r.startedAt) },
      { key: 'customer', label: 'Customer', render: (r) => r.customerName || '-' },
      { key: 'mobile', label: 'Mobile', render: (r) => r.customerMobile || '-' },
      { key: 'brand', label: 'Brand', render: (r) => r.brandName || '-' },
      {
        key: 'branch',
        label: 'Branch',
        render: (r) => `${r.salonName || '-'} (${r.branch || '-'})`
      },
      {
        key: 'mirror',
        label: 'Mirror',
        render: (r) => `${r.mirrorName || '-'} (${r.mirrorId || '-'})`
      },
      {
        key: 'amount',
        label: 'Amount',
        render: (r) => `₹${Number(r.totalAmount || 0).toLocaleString()}`
      },
      {
        key: 'services',
        label: 'Services',
        render: (r) => (
          <ExpandableText
            text={(r.services || []).map((s) => s.name).filter(Boolean).join(', ') || '-'}
            lines={2}
          />
        )
      },
      {
        key: 'actions',
        label: 'Actions',
        render: (r) => renderCancelButton(r._id)
      }
    ],
    [cancellingId]
  );

  const ongoingListFooter = (
    <>
      {loadingMore && <p className="admin-infinite-loading">Loading more…</p>}
      {!pagination.hasMore && rows.length > 0 && (
        <p className="admin-infinite-end">All rows loaded ({pagination.total} total).</p>
      )}
    </>
  );

  const hasActiveFilters = Boolean(filters.brandId || filters.salonId || filters.mirrorId || filters.branch);

  const ongoingEmptyState = (
    <div className="admin-card empty-state">
      <p>No ongoing services found.</p>
      <p style={{ marginTop: '8px', fontSize: '13px', color: '#8189A5' }}>
        {hasActiveFilters
          ? 'Try clearing filters or pick a different branch/mirror.'
          : 'Live sessions will show here when a customer starts a service on a mirror.'}
      </p>
      {hasActiveFilters && (
        <button type="button" className="btn btn-secondary btn-sm" style={{ marginTop: 12 }} onClick={handleClear}>
          Clear filters
        </button>
      )}
    </div>
  );

  const renderOngoingCard = (r) => (
    <div className="admin-card transaction-card">
      <div className="transaction-header">
        <div>
          <h3>#{String(r._id).slice(-8).toUpperCase()}</h3>
          <p className="transaction-date">Started: {formatDate(r.startedAt)}</p>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
          {renderCancelButton(r._id)}
          <span className="status-badge active">ongoing</span>
        </div>
      </div>
      <div className="transaction-details">
        <p><strong>Customer:</strong> {r.customerName}</p>
        <p><strong>Mobile:</strong> {r.customerMobile}</p>
        <p><strong>Brand:</strong> {r.brandName}</p>
        <p><strong>Branch:</strong> {r.salonName} ({r.branch})</p>
        <p><strong>Mirror:</strong> {r.mirrorName} ({r.mirrorId})</p>
        <p><strong>Amount:</strong> ₹{Number(r.totalAmount || 0).toLocaleString()}</p>
        <p><strong>Updated:</strong> {formatDate(r.updatedAt)}</p>
        <p>
          <strong>Services:</strong>{' '}
          <ExpandableText
            text={(r.services || []).map((s) => s.name).filter(Boolean).join(', ') || '-'}
            lines={2}
          />
        </p>
      </div>
    </div>
  );

  return (
    <AdminLayout
      pageTitle="Ongoing Services"
      subtitle={
        pagination.total
          ? `${rows.length} of ${pagination.total} in progress · scroll for more`
          : 'Live list of services currently in progress'
      }
    >
      <div className="admin-page-container">
        <div className="admin-page-header">
          <h1>Ongoing Services</h1>
        </div>

        <div className="admin-card filter-card">
          <h3>Filters</h3>
          <div className="filter-grid">
            <BrandBranchFilterFields
              portal={portal}
              filters={filters}
              onChange={(key, value) => {
                handleFilterChange(key, value);
                if (key === 'brandId' || key === 'salonId') {
                  handleFilterChange('mirrorId', '');
                  handleFilterChange('branch', '');
                }
              }}
              brands={brands}
              salons={salons}
            />
            <div className="form-group">
              <label>Mirror</label>
              <select
                value={filters.mirrorId}
                onChange={(e) => handleFilterChange('mirrorId', e.target.value)}
              >
                <option value="">All Mirrors</option>
                {mirrors
                  .filter((m) => !filters.salonId || m.salon?._id === filters.salonId || m.salon === filters.salonId)
                  .map((m) => (
                    <option key={m._id} value={m._id}>
                      {(m.mirrorCode || m.mirrorId || m.name || 'Mirror')} - {m.salon?.name || 'N/A'}
                    </option>
                  ))}
              </select>
            </div>
            <div className="form-group">
              <label title="Optional text label on a branch. Use Branch to pick a specific branch.">
                Location name
              </label>
              <select value={filters.branch} onChange={(e) => handleFilterChange('branch', e.target.value)}>
                <option value="">All location names</option>
                {branchOptions.map((b) => (
                  <option key={b} value={b}>
                    {b}
                  </option>
                ))}
              </select>
            </div>
            <div className="form-group filter-actions">
              <button className="btn btn-secondary" onClick={handleClear}>Clear</button>
            </div>
          </div>
        </div>

        {loading ? (
          <Loader variant="dark" fullScreen={false} />
        ) : (
          <>
            <AdminListViewControls
              viewMode={listView.viewMode}
              onViewModeChange={listView.setViewMode}
              selectedCount={listView.selectedCount}
              onSelectAll={listView.selectAll}
              onClearSelection={listView.clearSelection}
              allSelected={listView.allSelected}
              someSelected={listView.someSelected}
              totalCount={rows.length}
            showSelection={false}
            />
            <AdminSelectableList
              items={rows}
              getItemId={(r) => r._id}
              viewMode={listView.viewMode}
            selectable={false}
              isSelected={listView.isSelected}
              onToggleItem={listView.toggleItem}
              allSelected={listView.allSelected}
              someSelected={listView.someSelected}
              onSelectAll={listView.selectAll}
              onClearSelection={listView.clearSelection}
              columns={ongoingColumns}
              listClassName="transaction-list"
              renderGridCard={renderOngoingCard}
              emptyState={ongoingEmptyState}
              scrollRef={scrollRef}
              footer={ongoingListFooter}
            />
          </>
        )}
      </div>
    </AdminLayout>
  );
};

export default OngoingServices;
